import axios from 'axios';

// Dynamically determine API base URL based on current domain
const getApiBaseUrl = () => {
    const currentDomain = window.location.hostname;
    
    // Check for runtime config first (loaded from config.js)
    const runtimeConfig = window.ENV_CONFIG || {};
    
    if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
        // Development environment
        return runtimeConfig.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    } else if (currentDomain === 'admin.jaddid.ae') {
        // Production environment
        return runtimeConfig.REACT_APP_PROD_API_URL || process.env.REACT_APP_PROD_API_URL || 'https://api.jaddid.ae/api';
    } else if (currentDomain.includes('run.app')) {
        // Cloud Run environment (for testing before custom domain mapping)
        return runtimeConfig.REACT_APP_CLOUDRUN_API_URL || process.env.REACT_APP_CLOUDRUN_API_URL || 'https://jaddid-backend-123876256131.me-central1.run.app/api';
    } else {
        // Fallback - you can customize this based on your needs
        return runtimeConfig.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    }
};

// Create axios instance without baseURL - we'll set it in the interceptor
const api = axios.create({
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: false,
    timeout: 30000,
});

// Add auth token to requests AND set baseURL dynamically
api.interceptors.request.use((config) => {
    // Always determine API base URL at request time (not module load time)
    const apiBaseUrl = getApiBaseUrl();
    
    // Always override baseURL to ensure correct URL is used
    config.baseURL = apiBaseUrl;
    
    const token = localStorage.getItem('adminToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => {
       return response;
    },
    (error) => {
        console.error('API Error:', {
            status: error.response?.status,
            url: error.config?.url,
            message: error.message,
            data: error.response?.data
        });
        
        if (error.response?.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const adminLogin = async (credentials) => {
    try {
        const response = await api.post('/admin/login', credentials);
        return response.data;
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
};

// Dashboard APIs
export const getDashboardStats = async () => {
    try {
        const response = await api.get('/admin/dashboard/stats');
        return response.data;
    } catch (error) {
        console.error('Dashboard stats error:', error);
        throw error;
    }
};

export const getSystemHealth = async () => {
    try {
        const response = await api.get('/admin/system/health');
        return response.data;
    } catch (error) {
        console.error('System health error:', error);
        throw error;
    }
};

// User Management APIs
export const getUsers = async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
};

export const getAllUsers = async () => {
    const response = await api.get('/admin/users', { 
        params: { 
            page: 1, 
            limit: 10000 // Get all users by setting a very high limit
        } 
    });
    return response.data;
};

export const getUserStats = async () => {
    try {
        const response = await api.get('/admin/users/stats');
        return response.data;
    } catch (error) {
        console.error('User stats error:', error);
        return {
            total: 0,
            active: 0,
            inactive: 0,
            suspended: 0,
            recent: 0
        };
    }
};

export const getUserDetails = async (id) => {
    try {
        const response = await api.get(`/admin/users/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user details:', error);
        throw error;
    }
};

export const updateUserStatus = async (id, status) => {
    try {
        const response = await api.patch(`/admin/users/${id}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating user status:', error);
        throw error;
    }
};

export const updateUser = async (id, userData) => {
    try {
        const response = await api.put(`/admin/users/${id}`, userData);
        return response.data;
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};

export const deleteUser = async (id) => {
    try {
        const response = await api.delete(`/admin/users/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

export const resetUserPassword = async (id, options = {}) => {
    // options: { sendEmail?: boolean, newPassword?: string }
    try {
        const response = await api.post(`/admin/users/${id}/reset-password`, options);
        return response.data; // { message, temporaryPassword }
    } catch (error) {
        console.error('Error resetting user password:', error);
        throw error;
    }
};

export const resetVendorPassword = async (id, options = {}) => {
    // options: { sendEmail?: boolean, newPassword?: string }
    try {
        const response = await api.post(`/admin/vendors/${id}/reset-password`, options);
        return response.data; // { message, temporaryPassword }
    } catch (error) {
        console.error('Error resetting vendor password:', error);
        throw error;
    }
};

// Vendor Management APIs
export const getVendors = async (params = {}) => {
    const response = await api.get('/admin/vendors', { params });
    return response.data;
};

export const getVendorStats = async () => {
    const response = await api.get('/admin/vendors/stats');
    return response.data;
};

export const getAllVendors = async () => {
    const response = await api.get('/admin/vendors', { 
        params: { 
            page: 1, 
            limit: 10000 // Get all vendors by setting a very high limit
        } 
    });
    return response.data;
};

export const getVendorDetails = async (id) => {
    try {
        const response = await api.get(`/admin/vendors/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching vendor details:', error);
        // Return mock data
        return {
            id: id,
            name: 'Sample Vendor',
            contact_name: 'Contact Person',
            email: 'vendor@example.com',
            phone: '+971501234567',
            address: '123 Business Bay, Dubai',
            city: 'Dubai',
            emirate: 'Dubai',
            status: 'pending',
            buying_items: ['Electronics', 'Phones'],
            description: 'Sample vendor description',
            created_at: '2024-01-01T00:00:00Z'
        };
    }
};

export const updateVendorStatus = async (id, status) => {
    const response = await api.patch(`/admin/vendors/${id}/status`, { status });
    return response.data;
};

export const approveVendor = async (id) => {
    const response = await api.patch(`/admin/vendors/${id}/approve`);
    return response.data;
};

export const createVendor = async (formData) => {
    const response = await api.post('/admin/vendors', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const deleteVendor = async (id) => {
    const response = await api.delete(`/admin/vendors/${id}`);
    return response.data;
};

export const updateVendor = async (id, formData) => {
    const response = await api.put(`/admin/vendors/${id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

// Voucher Supplier Management APIs
export const getVoucherSuppliers = async (params = {}) => {
    try {
        const response = await api.get('/admin/voucher-suppliers', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher suppliers:', error);
        // Return mock data for development
        return {
            suppliers: [
                {
                    id: 1,
                    name: 'Sample Supplier',
                    contact_name: 'John Doe',
                    email: 'supplier@example.com',
                    phone: '+971501234567',
                    status: 'active',
                    voucher_count: 5,
                    created_at: '2024-01-01T00:00:00Z'
                }
            ]
        };
    }
};

export const getVoucherSupplierDetails = async (id) => {
    try {
        const response = await api.get(`/admin/voucher-suppliers/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher supplier details:', error);
        // Return mock data
        return {
            id: id,
            name: 'Sample Supplier',
            contact_name: 'John Doe',
            email: 'supplier@example.com',
            phone: '+971501234567',
            status: 'active',
            voucher_count: 5,
            active_vouchers: 3,
            description: 'Sample supplier description',
            created_at: '2024-01-01T00:00:00Z'
        };
    }
};

export const updateVoucherSupplierStatus = async (id, status) => {
    try {
        const response = await api.patch(`/admin/voucher-suppliers/${id}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating voucher supplier status:', error);
        return { success: true };
    }
};

// Voucher Management APIs
export const getAllVouchers = async (params = {}) => {
    try {
        const response = await api.get('/admin/vouchers', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching vouchers:', error);
        // Return mock data for development
        return {
            vouchers: [
                {
                    id: 1,
                    title: 'Sample Voucher',
                    code: 'SAMPLE2024',
                    supplier_name: 'Sample Supplier',
                    discount_value: 20,
                    status: 'active',
                    valid_until: '2024-12-31T00:00:00Z',
                    redemptions: 10,
                    description: 'Sample voucher description',
                    category: 'General'
                }
            ]
        };
    }
};

export const updateVoucherStatus = async (id, status) => {
    try {
        const response = await api.patch(`/admin/vouchers/${id}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating voucher status:', error);
        return { success: true };
    }
};

// Transaction Management APIs
export const getTransactions = async (params = {}) => {
    const response = await api.get('/admin/transactions', { params });
    return response.data;
};

export const getAllTransactions = async () => {
    const response = await api.get('/admin/transactions', { 
        params: { 
            page: 1, 
            limit: 10000 // Get all transactions by setting a very high limit
        } 
    });
    return response.data;
};

export const getTransactionDetails = async (id) => {
    try {
        const response = await api.get(`/admin/transactions/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching transaction details:', error);
        throw error;
    }
};

export const deleteSellingRequest = async (id) => {
    try {
        const response = await api.delete(`/admin/selling-requests/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting selling request:', error);
        throw error;
    }
};

// Analytics APIs
export const getUserAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/users', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching user analytics:', error);
        throw error;
    }
};

export const getVendorAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/vendors', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching vendor analytics:', error);
        throw error;
    }
};

export const getVoucherAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/vouchers', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher analytics:', error);
        throw error;
    }
};

export const getRevenueAnalytics = async (params = {}) => {
    try {
        const response = await api.get('/admin/analytics/revenue', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching revenue analytics:', error);
        throw error;
    }
};

// Transaction Management APIs
export const getSellingRequests = async (params = {}) => {
    const response = await api.get('/admin/selling-requests', { params });
    return response.data;
};

export const getSellingRequestDetails = async (id) => {
    const response = await api.get(`/admin/selling-requests/${id}`);
    return response.data;
};

export const updateSellingRequest = async (id, data) => {
    const response = await api.put(`/admin/selling-requests/${id}`, data);
    return response.data;
};

export const updateOfferStatus = async (id, status) => {
    const response = await api.patch(`/admin/offers/${id}/status`, { status });
    return response.data;
};

export const updateOffer = async (id, data) => {
    const response = await api.put(`/admin/offers/${id}`, data);
    return response.data;
};

export const deleteOffer = async (id) => {
    const response = await api.delete(`/admin/offers/${id}`);
    return response.data;
};

export const getTransactionStats = async () => {
    const response = await api.get('/admin/transactions/stats');
    return response.data;
};

// System Management APIs
export const getSystemLogs = async (params = {}) => {
    const response = await api.get('/admin/system/logs', { params });
    return response.data;
};

export const backupDatabase = async () => {
    const response = await api.post('/admin/system/backup');
    return response.data;
};

export const getSystemSettings = async () => {
    const response = await api.get('/admin/system/settings');
    return response.data;
};

export const updateSystemSettings = async (settings) => {
    const response = await api.put('/admin/system/settings', settings);
    return response.data;
};

// Admin Management APIs (Super Admin only)
export const getAdmins = async (params = {}) => {
    try {
        const response = await api.get('/admin/admins', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching admins:', error);
        throw error;
    }
};

export const getAdminDetails = async (id) => {
    try {
        const response = await api.get(`/admin/admins/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching admin details:', error);
        throw error;
    }
};

export const createAdmin = async (adminData) => {
    try {
        const response = await api.post('/admin/admins', adminData);
        return response.data;
    } catch (error) {
        console.error('Error creating admin:', error);
        throw error;
    }
};

export const updateAdminDetails = async (id, adminData) => {
    try {
        const response = await api.put(`/admin/admins/${id}`, adminData);
        return response.data;
    } catch (error) {
        console.error('Error updating admin:', error);
        throw error;
    }
};

export const deleteAdmin = async (id) => {
    try {
        const response = await api.delete(`/admin/admins/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting admin:', error);
        throw error;
    }
};

export const updateAdminPassword = async (id, password) => {
    try {
        const response = await api.patch(`/admin/admins/${id}/password`, { password });
        return response.data;
    } catch (error) {
        console.error('Error updating admin password:', error);
        throw error;
    }
};

// App Version Management APIs
export const getAppVersionSettings = async () => {
    try {
        const response = await api.get('/admin/app-versions');
        return response.data;
    } catch (error) {
        console.error('Error fetching app version settings:', error);
        throw error;
    }
};

export const updateAppVersionSettings = async (settings) => {
    try {
        const response = await api.put('/admin/app-versions', settings);
        return response.data;
    } catch (error) {
        console.error('Error updating app version settings:', error);
        throw error;
    }
};

export const updateIosVersionSettings = async (settings) => {
    try {
        const response = await api.put('/admin/app-versions/ios', settings);
        return response.data;
    } catch (error) {
        console.error('Error updating iOS version settings:', error);
        throw error;
    }
};

export const updateAndroidVersionSettings = async (settings) => {
    try {
        const response = await api.put('/admin/app-versions/android', settings);
        return response.data;
    } catch (error) {
        console.error('Error updating Android version settings:', error);
        throw error;
    }
};

export const getAppVersionHistory = async () => {
    try {
        const response = await api.get('/admin/app-versions/history');
        return response.data;
    } catch (error) {
        console.error('Error fetching app version history:', error);
        throw error;
    }
};

// Listing catalog management APIs
export const getCatalog = async () => {
    const response = await api.get('/admin/catalog');
    return response.data;
};

export const updateCatalog = async (catalog) => {
    const response = await api.put('/admin/catalog', catalog);
    return response.data;
};

// App Announcements APIs
export const getAnnouncements = async () => {
    try {
        const response = await api.get('/admin/announcements');
        return response.data;
    } catch (error) {
        console.error('Error fetching announcements:', error);
        throw error;
    }
};

export const createAnnouncement = async (data) => {
    try {
        const response = await api.post('/admin/announcements', data);
        return response.data;
    } catch (error) {
        console.error('Error creating announcement:', error);
        throw error;
    }
};

export const updateAnnouncement = async (id, data) => {
    try {
        const response = await api.put(`/admin/announcements/${id}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating announcement:', error);
        throw error;
    }
};

export const toggleAnnouncementStatus = async (id, isActive) => {
    try {
        const response = await api.patch(`/admin/announcements/${id}/status`, { is_active: isActive });
        return response.data;
    } catch (error) {
        console.error('Error toggling announcement status:', error);
        throw error;
    }
};

// Emirates Management APIs
export const getEmiratesSettings = async () => {
    try {
        const response = await api.get('/admin/emirates');
        return response.data;
    } catch (error) {
        console.error('Error fetching emirates settings:', error);
        throw error;
    }
};

export const updateEmirateStatus = async (emirateId, isEnabled) => {
    try {
        const response = await api.patch(`/admin/emirates/${emirateId}/status`, { is_enabled: isEnabled });
        return response.data;
    } catch (error) {
        console.error('Error updating emirate status:', error);
        throw error;
    }
};

export const updateEmirateOrder = async (emirateId, order) => {
    try {
        const response = await api.patch(`/admin/emirates/${emirateId}/order`, { order });
        return response.data;
    } catch (error) {
        console.error('Error updating emirate order:', error);
        throw error;
    }
};

export const bulkUpdateEmirateStatus = async (data) => {
    try {
        const response = await api.patch('/admin/emirates/bulk-status', data);
        return response.data;
    } catch (error) {
        console.error('Error bulk updating emirates:', error);
        throw error;
    }
};

// ============================================================================
// V2 C2C MARKETPLACE APIs
// ============================================================================

// --- C2C Listings ---
export const getListings = async (params = {}) => {
    const response = await api.get('/admin/listings', { params });
    return response.data;
};

export const getListingStats = async () => {
    const response = await api.get('/admin/listings/stats');
    return response.data;
};

export const getListingApprovalSettings = async () => {
    const response = await api.get('/admin/listings/approval-settings');
    return response.data;
};

export const updateListingApprovalSettings = async (listing_approval_enabled) => {
    const response = await api.patch('/admin/listings/approval-settings', { listing_approval_enabled });
    return response.data;
};

export const getListingFeeSettings = async () => {
    const response = await api.get('/admin/listings/fee-settings');
    return response.data;
};

// `free_listings_until` is an ISO date string, or null for no expiry.
export const updateListingFeeSettings = async (free_listings_enabled, free_listings_until = null) => {
    const response = await api.patch('/admin/listings/fee-settings', {
        free_listings_enabled,
        free_listings_until,
    });
    return response.data;
};

export const getPendingApprovalListings = async (params = {}) => {
    const response = await api.get('/admin/listings/pending-approval', { params });
    return response.data;
};

export const getListingCategories = async () => {
    const response = await api.get('/admin/listings/categories');
    return response.data;
};

export const getListingDetails = async (id) => {
    const response = await api.get(`/admin/listings/${id}`);
    return response.data;
};

export const updateListingStatus = async (id, status, reason = '') => {
    const response = await api.patch(`/admin/listings/${id}/status`, { status, reason });
    return response.data;
};

export const approveListing = async (id, notes = '') => {
    const response = await api.patch(`/admin/listings/${id}/approve`, { notes });
    return response.data;
};

export const rejectListing = async (id, reason, notes = '') => {
    const response = await api.patch(`/admin/listings/${id}/reject`, { reason, notes });
    return response.data;
};

export const bulkUpdateListingApprovals = async (listing_ids, action, reason = '', notes = '') => {
    const response = await api.patch('/admin/listings/bulk-approval', {
        listing_ids,
        action,
        reason,
        notes,
    });
    return response.data;
};

export const toggleListingFeatured = async (id, is_featured) => {
    const response = await api.patch(`/admin/listings/${id}/featured`, { is_featured });
    return response.data;
};

export const deleteAdminListing = async (id) => {
    const response = await api.delete(`/admin/listings/${id}`);
    return response.data;
};

export const bulkReactivateListings = async () => {
    const response = await api.post('/admin/listings/bulk-reactivate');
    return response.data;
};

// --- C2C Offers ---
export const getC2COffers = async (params = {}) => {
    const response = await api.get('/admin/c2c-offers', { params });
    return response.data;
};

export const getC2COfferStats = async () => {
    const response = await api.get('/admin/c2c-offers/stats');
    return response.data;
};

export const getC2COfferDetails = async (id) => {
    const response = await api.get(`/admin/c2c-offers/${id}`);
    return response.data;
};

export const deleteC2COffer = async (id) => {
    const response = await api.delete(`/admin/c2c-offers/${id}`);
    return response.data;
};

// --- Listing Reports ---
export const getListingReports = async (params = {}) => {
    const response = await api.get('/admin/listing-reports', { params });
    return response.data;
};

export const getListingReportStats = async () => {
    const response = await api.get('/admin/listing-reports/stats');
    return response.data;
};

export const resolveListingReport = async (id, status, action = null) => {
    const response = await api.patch(`/admin/listing-reports/${id}/resolve`, { status, action });
    return response.data;
};

// --- TangoPlus ---
export const getTangoQuotes = async (params = {}) => {
    const response = await api.get('/admin/tango-quotes', { params });
    return response.data;
};

export const getTangoStats = async () => {
    const response = await api.get('/admin/tango-quotes/stats');
    return response.data;
};

export const getTangoQuoteDetails = async (id) => {
    const response = await api.get(`/admin/tango-quotes/${id}`);
    return response.data;
};

// --- V2 Dashboard ---
export const getV2DashboardStats = async () => {
    const response = await api.get('/admin/dashboard/v2-stats');
    return response.data;
};

// --- User V2 Data ---
export const getUserListings = async (userId) => {
    const response = await api.get(`/admin/users/${userId}/listings`);
    return response.data;
};

export const getUserC2COffers = async (userId) => {
    const response = await api.get(`/admin/users/${userId}/c2c-offers`);
    return response.data;
};

export const getUserTangoQuotes = async (userId) => {
    const response = await api.get(`/admin/users/${userId}/tango-quotes`);
    return response.data;
};

// --- C2C Analytics ---
export const getC2CAnalytics = async (params = {}) => {
    const response = await api.get('/admin/analytics/c2c', { params });
    return response.data;
};

// --- Promo Codes ---
export const listPromoCodes = async () => {
    const response = await api.get('/admin/promo-codes');
    return response.data;
};

export const getPromoCode = async (id) => {
    const response = await api.get(`/admin/promo-codes/${id}`);
    return response.data;
};

export const createPromoCode = async (payload) => {
    const response = await api.post('/admin/promo-codes', payload);
    return response.data;
};

export const updatePromoCode = async (id, payload) => {
    const response = await api.patch(`/admin/promo-codes/${id}`, payload);
    return response.data;
};

export const deletePromoCode = async (id) => {
    const response = await api.delete(`/admin/promo-codes/${id}`);
    return response.data;
};

export default api;
